import Link from "next/link";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import format from "date-fns/format";
import parseJSON from "date-fns/parseJSON";
import type { Insight } from "models/insight";
import { twMerge } from "tailwind-merge";

import { faClock } from "@fortawesome/free-regular-svg-icons/faClock";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";

import ComposeButton from "components/ComposeButton";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";

import style from "./style.module.scss";

export const upstreamInsightsUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/insights/${slug}/upstream-insights`);
  url.searchParams.append("filter[aasm-state]", "published");
  url.searchParams.append(
    "fields[insights]",
    "slug,tagline,imperative,published-at"
  );
  url.searchParams.append("page[size]", "3");
  url.searchParams.append("sort", "-published-at");

  return url.href;
};

export const downstreamInsightsUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/insights/${slug}/downstream-insights`);
  url.searchParams.append("filter[aasm-state]", "published");
  url.searchParams.append(
    "fields[insights]",
    "slug,tagline,imperative,published-at"
  );
  url.searchParams.append("page[size]", "3");
  url.searchParams.append("sort", "published-at");

  return url.href;
};

export const originatedInsightsUrl = (slug: string) => {
  const url = new ApiEndpoint(`/api/v2/insights/${slug}/originated-insights`);
  url.searchParams.append("filter[aasm-state]", "published");
  url.searchParams.append(
    "fields[insights]",
    "slug,tagline,imperative,published-at"
  );

  return url.href;
};

type InsightStreamCardProps = {
  insight: Insight;
  hideTitle?: boolean;
  hideForkCta?: boolean;
  className?: string;
};

const InsightStreamCard = ({
  insight,
  hideTitle,
  hideForkCta,
  className,
}: InsightStreamCardProps) => {
  const { t } = useTranslation();

  const { account: loggedInAccount } = useAuthentication();
  const slug = insight.slug;

  const swrOptions = {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  };

  const { data: upstreamInsights } = useApi<{ data: Insight[] }>(
    upstreamInsightsUrl(slug),
    baseFetcher,
    swrOptions
  );
  const { data: downstreamInsights } = useApi<{ data: Insight[] }>(
    downstreamInsightsUrl(slug),
    baseFetcher,
    swrOptions
  );
  const { data: originatedInsights } = useApi<{ data: Insight[] }>(
    originatedInsightsUrl(slug),
    baseFetcher,
    swrOptions
  );

  const streamInsights = useMemo(() => {
    return [
      ...(insight ? [insight] : []),
      ...(downstreamInsights?.data || []).slice(0, 3),
      ...[
        ...(originatedInsights?.data || []),
        ...(upstreamInsights?.data || []),
      ].slice(0, 3),
    ]
      .reduce((insights: Insight[], item: Insight) => {
        if (insights.findIndex((i: any) => i.id === item.id) === -1) {
          return [...insights, item];
        }
        return insights;
      }, [])
      .sort((i: any, j: any) => {
        return i.publishedAt > j.publishedAt ? -1 : 1;
      });
  }, [
    insight,
    downstreamInsights?.data,
    originatedInsights?.data,
    upstreamInsights?.data,
  ]);

  return (
    <div className={`${style.container} ${className}`}>
      {!hideTitle && (
        <div className={style.header}>
          <label className={style.main_text}>{t("Insight Stream")}</label>
        </div>
      )}

      <div className={style.card_body}>
        <ul className={style.stream_list}>
          {streamInsights.length ? (
            streamInsights.map((item: any) => {
              const isSelf = item.id === insight.id;
              return (
                <li key={item.id} className={style.stream_item}>
                  <FontAwesomeIcon
                    icon={faCircle}
                    className={`${style.circle} ${style[item.imperative]}`}
                  />
                  {isSelf ? (
                    <span
                      className={twMerge(
                        style.tag_line,
                        style.self,
                        "break-words"
                      )}
                    >
                      {item.tagline}
                    </span>
                  ) : (
                    <Link
                      className="break-words"
                      href={`/insights/${item.slug}`}
                      passHref
                      prefetch={false}
                    >
                      <span className={`${style.tag_line} ${style.link}`}>
                        {item.tagline}
                      </span>
                    </Link>
                  )}
                  <div
                    className={`${style.published_at} ${isSelf && style.self}`}
                    suppressHydrationWarning
                  >
                    <FontAwesomeIcon icon={faClock} />
                    {item.publishedAt &&
                      format(parseJSON(item.publishedAt), "dd MMM yyyy")}
                  </div>
                </li>
              );
            })
          ) : (
            <li> Loading... </li>
          )}
        </ul>
      </div>

      {loggedInAccount?.canComposeInsight && !hideForkCta && (
        <div className="p-[15px]">
          <ComposeButton
            forkFrom={insight}
            className={twMerge(
              "text-[14px] font-medium text-smart-teal hover:text-dark-teal",
              "cursor-pointer"
            )}
          >
            Fork this Insight
          </ComposeButton>
        </div>
      )}
    </div>
  );
};

export default InsightStreamCard;
